import i18next from "i18next";
import { LOADING, SET_CURRENT_WEBSITE, SET_CURRENT_WEBSITE_DATA, SET_CURRENT_WEBSITE_PLAN_DATA } from "../../Actions/Types";
import { DomainDetailsApi } from "../../Api/DomainApi/DomainDetailsApi"
import { getViolationsCountApi, getViolationsListApi } from "../../Api/ViolationsApi";
import parseISODate from "../../Helpers/parseISODate";

type PlanDetailsType = {
    isExpired: boolean;
    isTrial: boolean;
    isInit: boolean;
    isFreeWidget: boolean;
    isProPlan: boolean;
    isCanceledPlan: boolean;
    planName: string | null;
    planType: string | null;
    planExpiredData: string | null;
};

// service to get domain details and store
const DomainDetailsService = async (
    token: string,
    websiteID: number,
    dispatch: Function,
): Promise<any> => {
        dispatch({ type: LOADING, payload: true });

    try {
        const domainResponse = await DomainDetailsApi(token, websiteID);

        if (domainResponse.status !== 200 || !domainResponse.Data?.length) {
            throw new Error("Domain details fetch failed");
        }

        const [
            scanDataRes,
            scanErrorViolationRes,
            scanSuccessViolationRes
        ] = await Promise.all([
            getViolationsCountApi(token, websiteID),
            getViolationsListApi(token, websiteID, i18next.language, 0),
            getViolationsListApi(token, websiteID, i18next.language, 1)
        ]);

        // Validate all required responses
        if (
            scanDataRes.status !== 200 ||
            scanErrorViolationRes.status !== 200 ||
            scanSuccessViolationRes.status !== 200
        ) {
            throw new Error("One or more API calls failed");
        }

        // === Process metrics ===
        const {
            successAccessibilityChecks,
            failingAccessibilityChecks,
            total_update_alt_text,
            missing_alt_text
        } = scanDataRes.data[0];

        const accessibilityScore =
            successAccessibilityChecks + failingAccessibilityChecks > 0
                ? parseFloat(
                      (
                          (successAccessibilityChecks /
                              (successAccessibilityChecks + failingAccessibilityChecks)) *
                          100
                      ).toFixed(2)
                  )
                : 0;

        const successViolations = scanSuccessViolationRes.total_counts;
        const failedViolations = scanErrorViolationRes.total_counts;

        const violationsRemediatedPercentage =
            successViolations + failedViolations > 0
                ? parseFloat(
                      (
                          (successViolations / (successViolations + failedViolations)) *
                          100
                      ).toFixed(2)
                  )
                : 0;

        const imageAltRemediatedPercentage =
            total_update_alt_text + missing_alt_text > 0
                ? parseFloat(
                      (
                          (total_update_alt_text /
                              (total_update_alt_text + missing_alt_text)) *
                          100
                      ).toFixed(2)
                  )
                : 0;

        dispatch({
            type: SET_CURRENT_WEBSITE_DATA,
            payload: {
                accessibilityScore,
                violationsRemediatedPercentage,
                successViolations,
                failedViolations,
                imageAltRemediatedPercentage,
                successImageAlt: total_update_alt_text,
                failedImageAlt: missing_alt_text,
                successViolationsList: scanSuccessViolationRes.Data,
                failedViolationsList: scanErrorViolationRes.Data,
            }
        });

        // === Plan Info Calculation ===
        const domain = domainResponse.Data[0];
        let planData: PlanDetailsType = {
            isExpired: false,
            isTrial: false,
            isInit: successViolations === 0 && failedViolations === 0,
            isFreeWidget: false,
            isProPlan: false,
            isCanceledPlan: false,
            planName: null,
            planType: null,
            planExpiredData: null,
        };

        const parsePlanInfo = (plan: any) => ({
            planName: plan.name,
            planType: plan.platforms,
            planExpiredData: parseISODate(plan.end_date),
        });

        if (domain.is_expired === 1 && domain.expired_package_detail?.length) {
            planData = {
                ...planData,
                isExpired: true,
                ...parsePlanInfo(domain.expired_package_detail[0])
            };
        } else if (domain.is_expired === 3 && domain.current_package_only?.length) {
            planData = {
                ...planData,
                isTrial: true,
                ...parsePlanInfo(domain.current_package_only[0])
            };
        } else if (domain.canceled_package_only?.length) {
            planData = {
                ...planData,
                isCanceledPlan: true,
                ...parsePlanInfo(domain.canceled_package_only[0])
            };
        } else if (domain.current_package_only?.length) {
            const current = domain.current_package_only[0];
            if (current.is_free_widget === 1) {
                planData = {
                    ...planData,
                    isFreeWidget: true,
                    ...parsePlanInfo(current)
                };
            } else if (current.cancel_date) {
                planData = {
                    ...planData,
                    isCanceledPlan: true,
                    ...parsePlanInfo(current)
                };
            } else {
                planData = {
                    ...planData,
                    isProPlan: true,
                    ...parsePlanInfo(current)
                };
            }
        }

        dispatch({
            type: SET_CURRENT_WEBSITE_PLAN_DATA,
            payload: planData
        });

        dispatch({
            type: SET_CURRENT_WEBSITE,
            payload: domain
        });

            dispatch({ type: LOADING, payload: false });

        // ✅ Return same response for .then() in caller
        return domainResponse;
    } catch (error) {
        console.error("DomainDetailsService Error:", error);
        dispatch({ type: LOADING, payload: false });
        return { status: 400 };
    }
};

/* const DomainDetailsWithoutViolationsService = async (
    token: string,
    websiteID: number,
    dispatch: Function,
    t: Function
): Promise<any> => {

    // Set Loading
    dispatch({
        type: LOADING,
        payload: true,
    });

    // Domain Details
    const DomainDetailsApiRes = DomainDetailsApi(token, websiteID).then(async (domainResponse) => {

        // Check Domain Details Res
        if (domainResponse.status === 200) {

            // Set Current Website Data
            dispatch({
                type: SET_CURRENT_WEBSITE,
                payload: domainResponse.Data[0]
            });

            // Set Loading
            dispatch({
                type: LOADING,
                payload: false,
            });

            return { status: 200 }

        } else {

            // Set Loading
            dispatch({
                type: LOADING,
                payload: false,
            });

            return { status: 400 }
        }

    })
    return DomainDetailsApiRes;

} */

export { DomainDetailsService }
